'use client';

import { useEffect, useRef, type RefObject } from 'react';

export interface DisclosureMotionOptions {
  /**
   * Selector for the answer panel inside each `<details>`, resolved against
   * the row. The panel is the element whose height animates, so it must wrap
   * the whole answer and nothing else. Defaults to `[data-faq-answer]`.
   */
  panelSelector?: string;
  /**
   * Called once per intentional expansion with the row's `id`, before the
   * rows it displaces start to close. Analytics belong here, in the page: the
   * hook imports no event module, and a page keeps its own dedupe policy for
   * the native `toggle` path (record on `toggle` only while `data-expanded`
   * is absent, which is exactly when this enhancement is inactive).
   */
  onExpand?: (id: string) => void;
}

const DEFAULT_PANEL_SELECTOR = '[data-faq-answer]';

/**
 * Answer-height motion for a list of native `<details>` disclosures (the
 * "local panel transition" in docs/ai-rules/halcy-landing-motion.md), with
 * one row open at a time. The page owns the markup and the skin; this hook
 * owns the transition and the state it needs to announce.
 *
 * Markup contract: every `<details>` under `root` carries a `<summary>` and
 * one panel matching `panelSelector`. While the enhancement is active the hook
 * writes `data-expanded="true" | "false"` on the row (the row's intent, set at
 * the start of a transition, so a stylesheet can show the closed indicator on
 * a row whose native `open` is still set while it closes), `aria-expanded` on
 * the summary and `inert` on a closed or closing panel. A `name` attribute is
 * suspended while the hook is active and restored on cleanup: the browser's
 * exclusive group would hide the previous answer before it could close, so
 * the hook closes siblings itself. Rows without a `name` are left without one.
 *
 * The enhancement is only installed when both `Element.prototype.animate` and
 * `window.matchMedia` exist; otherwise nothing is touched and the disclosures
 * stay native, which is the no-JavaScript rendering as well.
 *
 * Reduced motion settles instantly, both when it is already requested and
 * when it changes mid-transition. A resize or a late font load also settles,
 * because a filled animation pins the panel at a height measured before
 * either event.
 */
export function useDisclosureMotion(
  root: RefObject<HTMLElement | null>,
  { panelSelector = DEFAULT_PANEL_SELECTOR, onExpand }: DisclosureMotionOptions = {}
): void {
  // The click handler reads the latest callback, so an inline arrow at the call
  // site never re-arms the enhancement (which would settle every row mid-flight).
  const expand = useRef(onExpand);
  useEffect(() => {
    expand.current = onExpand;
  }, [onExpand]);

  useEffect(() => {
    const list = root.current;
    if (
      !list ||
      typeof Element.prototype.animate !== 'function' ||
      typeof window.matchMedia !== 'function'
    )
      return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const items = Array.from(list.querySelectorAll('details'), (row) => {
      const summary = row.querySelector('summary');
      const panel = row.querySelector<HTMLElement>(panelSelector);
      if (!summary || !panel) throw new Error('Disclosure is missing its summary or answer panel');
      return {
        row,
        summary,
        panel,
        name: row.getAttribute('name'),
        expanded: row.open,
        animation: null as Animation | null,
      };
    });
    type Disclosure = (typeof items)[number];

    const cancel = (item: Disclosure): void => {
      if (!item.animation) return;
      item.animation.onfinish = null;
      item.animation.cancel();
      item.animation = null;
    };
    const settle = (item: Disclosure): void => {
      cancel(item);
      item.row.open = item.expanded;
      item.row.dataset.expanded = String(item.expanded);
      item.summary.setAttribute('aria-expanded', String(item.expanded));
      item.panel.inert = !item.expanded;
      item.panel.style.removeProperty('height');
      item.panel.style.removeProperty('overflow');
    };
    const transition = (item: Disclosure, expanded: boolean): void => {
      // Sample the in-flight frame before canceling, so rapid clicks never jump.
      const start = item.row.open ? item.panel.getBoundingClientRect().height : 0;
      item.expanded = expanded;
      item.row.dataset.expanded = String(expanded);
      item.summary.setAttribute('aria-expanded', String(expanded));
      if (!expanded && item.panel.contains(document.activeElement)) item.summary.focus();
      item.panel.inert = !expanded;
      cancel(item);
      if (reduced.matches) {
        settle(item);
        return;
      }
      // Keep details open until its closing animation finishes.
      item.row.open = true;
      item.panel.style.removeProperty('height');
      const end = expanded ? item.panel.getBoundingClientRect().height : 0;
      if (Math.abs(end - start) < 1) {
        settle(item);
        return;
      }
      item.panel.style.height = `${start}px`;
      item.panel.style.overflow = 'hidden';
      const animation = item.panel.animate([{ height: `${start}px` }, { height: `${end}px` }], {
        duration: 250,
        easing: 'ease',
        fill: 'both',
      });
      item.animation = animation;
      animation.onfinish = () => {
        if (item.animation === animation) settle(item);
      };
    };

    const listeners = items.map((item) => {
      // Native name exclusivity would hide the previous answer before it can close.
      item.row.removeAttribute('name');
      settle(item);
      const onClick = (event: MouseEvent): void => {
        event.preventDefault();
        const expanded = !item.expanded;
        if (expanded) {
          for (const other of items) {
            if (other !== item && other.expanded) transition(other, false);
          }
          expand.current?.(item.row.id);
        }
        transition(item, expanded);
      };
      item.summary.addEventListener('click', onClick);
      return () => item.summary.removeEventListener('click', onClick);
    });
    let disposed = false;
    const settleAll = (): void => {
      if (!disposed) items.forEach(settle);
    };
    reduced.addEventListener('change', settleAll);
    window.addEventListener('resize', settleAll);
    void document.fonts?.ready.then(settleAll);

    return () => {
      settleAll();
      disposed = true;
      listeners.forEach((remove) => remove());
      reduced.removeEventListener('change', settleAll);
      window.removeEventListener('resize', settleAll);
      for (const item of items) {
        delete item.row.dataset.expanded;
        item.summary.removeAttribute('aria-expanded');
        item.panel.inert = false;
        if (item.name !== null) item.row.setAttribute('name', item.name);
      }
    };
  }, [root, panelSelector]);
}
